import Link from 'next/link';
import { Trans, useTranslation } from 'next-i18next';
import type { MouseEvent as ReactMouseEvent } from 'react';
import { useState } from 'react';
import { FaCircleQuestion, FaPlus } from 'react-icons/fa6';

import { BTN_PRIMARY_SM, BTN_SECONDARY_SM } from '@/constants/button-classes';
import type { FaqItem } from '@/constants/faq-items';

const EYEBROW_CLASS = 'inline-flex items-center gap-2 font-mono text-[11px] font-bold uppercase tracking-[0.22em] text-[var(--np-green)] max-[640px]:text-[10px] max-[640px]:tracking-[0.18em]';
const HEADING_CLASS = 'text-[56px] font-bold leading-none tracking-[-0.03em] text-white max-[1199px]:text-[clamp(40px,5.5vw,56px)] max-[960px]:!text-[clamp(34px,5.2vw,52px)] max-[960px]:leading-[1.05] max-[640px]:!text-[clamp(28px,8vw,38px)] max-[380px]:!text-[clamp(26px,9vw,34px)] [&_em]:not-italic [&_em]:text-[var(--np-green)]';

interface FaqJsonLd {
  '@context': 'https://schema.org';
  '@type': 'FAQPage';
  mainEntity: {
    '@type': 'Question';
    name: string;
    acceptedAnswer: { '@type': 'Answer'; text: string };
  }[];
}

function buildFaqJsonLd(items: FaqItem[]): string {
  const payload: FaqJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: items.map((item) => ({
      '@type': 'Question',
      name: item.q,
      acceptedAnswer: { '@type': 'Answer', text: item.a },
    })),
  };
  return JSON.stringify(payload);
}

export function ServersFaq() {
  const { t } = useTranslation('pages/servers');
  const [openIdx, setOpenIdx] = useState(-1);

  const items = t('v2.faq.items', { returnObjects: true }) as FaqItem[];
  const faqItems = Array.isArray(items) ? items : [];
  const faqJsonLd = buildFaqJsonLd(faqItems);

  const onSummaryClick = (event: ReactMouseEvent<HTMLElement>, index: number) => {
    event.preventDefault();
    setOpenIdx(openIdx === index ? -1 : index);
  };

  return (
    <section id='faq-servidores' aria-labelledby='servers-faq-heading' className='relative scroll-mt-[96px]'>
      <script type='application/ld+json' dangerouslySetInnerHTML={{ __html: faqJsonLd }} />
      <div className='mx-auto flex w-full max-w-[1400px] flex-col gap-10 p-5 xl:pb-20 xl:pt-10'>
        <div className='grid items-start gap-5 max-[900px]:grid-cols-1 max-[900px]:gap-12 min-[901px]:grid-cols-[1fr_1.15fr]'>
          <div className='flex flex-col gap-5'>
            <p className={EYEBROW_CLASS}>
              <FaCircleQuestion size={14} aria-hidden='true' />
              {t('v2.faq.eyebrow')}
            </p>
            <h2 id='servers-faq-heading' className={HEADING_CLASS}>
              <Trans i18nKey='v2.faq.title' t={t} components={{ em: <em /> }} />
            </h2>
            <p className='text-[clamp(15px,1.3vw,18px)] leading-[1.7] text-[var(--np-fg-dim)]'>{t('v2.faq.snippet')}</p>
            <div className='flex flex-col gap-5 lg:flex-row'>
              <Link href='/trial' className={BTN_PRIMARY_SM}>
                {t('v2.faq.primaryCta')}
              </Link>
              <Link href='/support' className={BTN_SECONDARY_SM}>
                {t('v2.faq.supportCta')}
              </Link>
            </div>
          </div>

          <ul className='flex min-w-0 list-none flex-col gap-2.5'>
            {faqItems.map((item, index) => {
              const isOpen = openIdx === index;
              return (
                <li key={item.q}>
                  <details data-testid='faq-item' open={isOpen} className='group overflow-hidden rounded-xl border border-[var(--np-line)] bg-white/[0.02] transition-[border-color,background] duration-200 open:border-[rgba(156,255,46,0.35)] open:bg-[rgba(156,255,46,0.05)] hover:border-[rgba(156,255,46,0.2)] hover:bg-[rgba(156,255,46,0.03)]'>
                    <summary onClick={(event) => onSummaryClick(event, index)} className='flex w-full cursor-pointer list-none items-center justify-between gap-5 p-6 text-start [&::-webkit-details-marker]:hidden'>
                      <h3 className='font-mono text-[13px] font-semibold uppercase leading-snug tracking-widest text-[var(--np-fg)]'>{item.q}</h3>
                      <span aria-hidden='true' className='inline-flex size-8 shrink-0 items-center justify-center rounded-full border border-[var(--np-line)] bg-white/[0.04] text-[var(--np-fg-dim)] transition-[background,color,border-color,transform] duration-200 group-open:rotate-45 group-open:border-[var(--np-green)] group-open:bg-[rgba(156,255,46,0.12)] group-open:text-[var(--np-green)] group-hover:border-[rgba(156,255,46,0.3)] group-hover:text-[var(--np-green)]'>
                        <FaPlus size={14} />
                      </span>
                    </summary>
                    <p className='px-6 pb-[22px] text-sm leading-[1.65] text-[var(--np-fg-dim)]'>{item.a}</p>
                  </details>
                </li>
              );
            })}
          </ul>
        </div>
      </div>
    </section>
  );
}
